import type { MessageDescriptor } from 'react-intl';

import { engravingMessages } from '@/messages/engraving';

const isLeapYear = (year: number): boolean => {
  // Treat 2-digit years validation to work globally. 00 will be treated as true as it depends of the century.
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
};

const getMaxDayForMonth = (month: number, year?: number): number => {
  if (month === 2) {
    if (typeof year === 'number') {
      return isLeapYear(year) ? 29 : 28;
    }
    return 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
};

const isValidDay = (d: string): boolean => {
  if (!d) return true;
  if (d.length > 2) return false;
  const day = Number(d);
  return day >= 1 && day <= 31;
};

const isValidMonth = (m: string): boolean => {
  if (!m) return true;
  if (m.length > 2) return false;
  const month = Number(m);
  return month >= 1 && month <= 12;
};

const isValidYearPrefix = (y: string): boolean => {
  if (!y) return true;
  if (y.length > 4 || y.length === 3 || y.length === 1) return false;
  if (y.length === 2) return true;
  if (y.length === 4) {
    const year = Number(y);
    if (year < 1 || year > 2999) return false;
    return true;
  }
  return false;
};

type DateValidationResult = {
  message?: MessageDescriptor;
  valid: boolean;
};

const hasOnlyDigitsAndDots = (value: string): boolean =>
  /^[0-9.]*$/.test(value);

const exceedsMaxDateLength = (value: string): boolean => value.length > 10; // dd.mm.yyyy

const tooManySegments = (parts: string[]): boolean => parts.length > 3;

const countDigits = (segments: string[]): number =>
  segments.join('').replaceAll(/\D/g, '').length;

const segmentsHaveTooManyDigits = (
  parts: string[],
  seg1: string,
  seg2: string,
  seg3: string,
): boolean => {
  if (parts.length === 1) return seg1.length > 4;
  if (parts.length === 2) return seg1.length > 2 || seg2.length > 4;
  // parts.length === 3
  return seg1.length > 2 || seg2.length > 2 || seg3.length > 4;
};

interface InterpretedSegments {
  dayStr: string;
  monthStr: string;
  yearStr: string;
}

const interpretSegments = (
  parts: string[],
): InterpretedSegments | DateValidationResult => {
  const [seg1 = '', seg2 = '', seg3 = ''] = parts;
  let dayStr = '';
  let monthStr = '';
  let yearStr = parts.length === 1 ? seg1 : '';

  if (parts.length === 3) {
    // dd.mm.yyyy — allow 1–2 digits for day and month, 4 or 2 for year (2 maps to 20xx)
    dayStr = seg1;
    monthStr = seg2;
    yearStr = seg3;
  } else if (parts.length === 2) {
    // Two segments can be:
    // - m{1,2}.yyyy (month with 4-digit year)
    // - mm.yy       (month and 2-digit year; allow 00–99)
    // - dd.mm       (day and month, both 2 digits) — when seg1 > 12
    if (seg2.length === 4) {
      monthStr = seg1;
      yearStr = seg2;
    } else if (seg2.length === 2) {
      const seg1Num = Number(seg1);
      const seg1IsValidMonth = isValidMonth(seg1);

      if (seg1IsValidMonth) {
        monthStr = seg1;
        yearStr = seg2; // two-digit year
      } else if (seg1.length === 2 && isValidMonth(seg2) && seg1Num > 12) {
        dayStr = seg1;
        monthStr = seg2;
      } else if (!seg1IsValidMonth && !isValidMonth(seg2)) {
        return {
          message: engravingMessages.invalidMonth,
          valid: false,
        };
      } else {
        return {
          message: engravingMessages.useDdMmOrMmYyOrMYyyy,
          valid: false,
        };
      }
    } else {
      return {
        message: engravingMessages.useDdMmOrMmYyOrMYyyy,
        valid: false,
      };
    }
  } else if (parts.length === 1) {
    if (seg1.length !== 4) {
      return {
        message: engravingMessages.yearMustBe4Digits,
        valid: false,
      };
    }
  }

  return { dayStr, monthStr, yearStr };
};

const validateSegmentValues = (
  dayStr: string,
  monthStr: string,
  yearStr: string,
): DateValidationResult | null => {
  if (!isValidDay(dayStr))
    return {
      message: engravingMessages.invalidDay,
      valid: false,
    };
  if (!isValidMonth(monthStr))
    return {
      message: engravingMessages.invalidMonth,
      valid: false,
    };
  if (!isValidYearPrefix(yearStr)) {
    return {
      message: engravingMessages.invalidYear,
      valid: false,
    };
  }
  return null;
};

const crossFieldValidate = (
  dayStr: string,
  monthStr: string,
  yearStr: string,
): DateValidationResult | null => {
  if (!(monthStr.length >= 1 && dayStr.length >= 1)) return null;

  const month = Number(monthStr);
  if (!(month >= 1 && month <= 12 && dayStr.length === 2)) return null;

  const inferredYear: number | undefined =
    yearStr.length === 3 ? undefined : Number(yearStr);
  const maxDay = getMaxDayForMonth(month, inferredYear);
  const day = Number(dayStr);
  if (day <= maxDay) return null;

  if (
    month === 2 &&
    day === 29 &&
    typeof inferredYear === 'number' &&
    !isLeapYear(inferredYear)
  ) {
    return { message: engravingMessages.leapYearRequired, valid: false };
  }
  return { message: engravingMessages.invalidDayForMonth, valid: false };
};

/**
 * Validates the engraving date input value according to the following rules:
 * - Only digits and dots are allowed
 * - The date must be at least 4 digits long
 * - The date must be 8 digits long maximum
 * - The date must be a valid date
 * - The date is not a valid leap year date
 * - The date must not have more than 3 segments
 * - The date must be in on of those formats:
 *
 *  - dd.mm
 *
 *  - mm.yy
 *  - d.m.yy
 *  - dd.m.yy
 *  - d.mm.yy
 *  - dd.mm.yy
 *
 *  - m.yyyy
 *  - mm.yyyy
 *  - d.m.yyyy
 *  - d.mm.yyyy
 *  - dd.m.yyyy
 *  - dd.mm.yyyy
 */
export const validateEngravingDate = (value: string): DateValidationResult => {
  if (!value) {
    return {
      message: engravingMessages.dateMustBeDdMmYyyy,
      valid: false,
    };
  }

  if (!hasOnlyDigitsAndDots(value)) {
    return {
      message: engravingMessages.onlyDigitsAndDots,
      valid: false,
    };
  }

  if (exceedsMaxDateLength(value)) {
    return {
      message: engravingMessages.dateMustBeDdMmYyyy,
      valid: false,
    };
  }

  const parts = value.split('.');
  if (tooManySegments(parts)) {
    return {
      message: engravingMessages.dateMustBeDdMmYyyy,
      valid: false,
    };
  }

  const [seg1 = '', seg2 = '', seg3 = ''] = parts;

  // Enforce minimum total digits: at least 4
  const totalDigits = countDigits([seg1, seg2, seg3]);
  if (totalDigits < 4) {
    return {
      message: engravingMessages.enterAtLeast4Digits,
      valid: false,
    };
  }

  // Segment max-lengths depend on how many parts we have
  if (segmentsHaveTooManyDigits(parts, seg1, seg2, seg3)) {
    return {
      message: engravingMessages.tooManyDigitsInPart,
      valid: false,
    };
  }

  const interpreted = interpretSegments(parts);
  if ('valid' in interpreted) return interpreted;
  const { dayStr, monthStr, yearStr } = interpreted;

  const segmentValueError = validateSegmentValues(dayStr, monthStr, yearStr);
  if (segmentValueError) return segmentValueError;

  const crossFieldError = crossFieldValidate(dayStr, monthStr, yearStr);
  if (crossFieldError) return crossFieldError;

  return {
    valid: true,
  };
};

type Coordinates = { x1: number; x2: number; y1: number; y2: number };

export const sanitizeCoordinates = (rawCoordinates: string): string => {
  if (!rawCoordinates) return '';
  return rawCoordinates
    .trim()
    .replaceAll(/[\u201C\u201D]/g, '"') // curly double quotes → standard
    .replaceAll(/[\u2018\u2019]/g, "'");
};

export const parseCoordinates = (raw: unknown): Coordinates | null => {
  if (!raw) return null;
  try {
    let value: unknown = raw;
    if (typeof raw === 'string') {
      const normalized = sanitizeCoordinates(raw);
      value = JSON.parse(normalized);
    }

    const { x1, x2, y1, y2 } = value as Coordinates;
    if (
      [x1, x2, y1, y2].every((n) => typeof n === 'number' && Number.isFinite(n))
    ) {
      return { x1, x2, y1, y2 };
    }
  } catch {
    return null;
  }
  return null;
};
